import { useState } from "react";
import { apiPost } from "../lib/api";
import { BRAND } from "../lib/brand";
import { parseTeamsRosters } from "../lib/teamRosterCsv";

/* ───────────────────── TEAM + ROSTER CSV UPLOAD ─────────────────────
   Parses a CSV of teams+players, previews it, then creates each team and
   its roster via the existing /teams-manage and /roster endpoints. */
const UPLOAD_CONCURRENCY = 5;

export function TeamRosterUpload({ adminCode, season, seasonStartMonth, seasonStartYear, seasonEndMonth, seasonEndYear, onTeamsChanged, onClose }) {
  const [fileName, setFileName] = useState("");
  const [parsed, setParsed] = useState(null);     // result of parseTeamsRosters
  const [committing, setCommitting] = useState(false);
  const [progress, setProgress] = useState(null);  // { done, total, label }
  const [results, setResults] = useState(null);     // summary after commit

  const handleText = (text, name) => {
    setResults(null);
    setProgress(null);
    setFileName(name || "");
    setParsed(parseTeamsRosters(text));
  };

  const handleFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => handleText(String(reader.result), f.name);
    reader.readAsText(f);
  };

  const reset = () => {
    setParsed(null); setFileName(""); setResults(null); setProgress(null);
  };

  // Create one team then its roster. Per-team work stays ordered (the roster
  // needs the team to exist); teams run concurrently via the pool below.
  const processTeam = async (t, summary) => {
    let teamOk = true;
    try {
      await apiPost("/teams-manage", {
        name: t.name,
        passcode: t.parentPasscode,
        coachPasscode: t.coachPasscode,
        ageGroup: t.ageGroup,
        season: (season || "").trim(),
        seasonStartMonth, seasonStartYear, seasonEndMonth, seasonEndYear,
      }, null, adminCode);
      summary.created.push(t.name);
    } catch (e) {
      if (/already exists/i.test(e.message || "")) {
        summary.existing.push(t.name);
      } else {
        summary.failed.push({ name: t.name, error: e.message || "create failed" });
        teamOk = false;
      }
    }

    // Add roster to created or pre-existing teams (skip only if the team truly failed).
    if (teamOk && t.players.length > 0) {
      try {
        await apiPost("/roster/" + t.id, {
          players: t.players.map((p) => ({
            name: p.name,
            jerseyNumber: p.jersey || "",
            parentEmail: p.parentEmail || "",
          })),
        }, null, adminCode);
        summary.playersAdded += t.players.length;
      } catch (e) {
        summary.playerErrors.push({ name: t.name, error: e.message || "roster failed" });
      }
    }
  };

  const commit = async () => {
    if (!parsed || parsed.teams.length === 0) return;
    setCommitting(true);
    const summary = { created: [], existing: [], failed: [], playersAdded: 0, playerErrors: [] };
    const teams = parsed.teams;
    let done = 0;
    setProgress({ done: 0, total: teams.length });

    // Concurrency-limited worker pool. `next++` is atomic (no await between
    // read and increment), so each team is claimed exactly once.
    let next = 0;
    const runWorker = async () => {
      while (next < teams.length) {
        const t = teams[next++];
        await processTeam(t, summary);
        done++;
        setProgress({ done, total: teams.length });
      }
    };
    const poolSize = Math.min(UPLOAD_CONCURRENCY, teams.length);
    await Promise.all(Array.from({ length: poolSize }, runWorker));

    setProgress({ done: teams.length, total: teams.length });
    setResults(summary);
    setCommitting(false);
    if (onTeamsChanged) onTeamsChanged();
  };

  const teams = parsed ? parsed.teams : [];
  const errors = parsed ? parsed.errors : [];

  return (
    <div className="mt-4 pt-4 border-t border-gray-100">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-gray-700">Upload Teams + Rosters (CSV)</h4>
        <button onClick={onClose} className="text-xs text-gray-400 hover:text-gray-600">Close</button>
      </div>

      <p className="text-xs text-gray-500 mb-3">
        One row per player: <code className="bg-gray-100 px-1 rounded">Team Name, Age Group, Parent Passcode, Coach Passcode, Player, Jersey, Parent Email</code>.
        Blank passcodes auto-generate. Season dates above apply to all teams. Parent
        emails are optional here — you can add them per player from the Roster button later,
        and a blank column never wipes an email that's already on file.{" "}
        <a href="/templates/teams-rosters-template.csv" download className="text-blue-600 hover:underline">Download template</a>
        {" · "}
        <a href="/templates/teams-rosters-blank.csv" download className="text-blue-600 hover:underline">Blank</a>
      </p>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <label className="px-4 py-2 text-sm font-medium text-white rounded-xl cursor-pointer shadow-sm" style={{ backgroundColor: BRAND.primary }}>
          Choose CSV file…
          <input type="file" accept=".csv,.txt,text/csv" onChange={handleFile} className="hidden" />
        </label>
        {fileName && <span className="text-xs text-gray-500">{fileName}</span>}
        {parsed && (
          <button onClick={reset} className="text-xs text-gray-400 hover:text-gray-600 underline">clear</button>
        )}
      </div>

      <details className="mb-3">
        <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">…or paste rows instead</summary>
        <textarea
          onChange={(e) => handleText(e.target.value, "")}
          placeholder={"Team Name,Age Group,Parent Passcode,Coach Passcode,Player,Jersey\nSartell U10 Blue,U10,,,Alex Johnson,7"}
          className="w-full mt-2 px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400 min-h-[90px]"
        />
      </details>

      {/* Parse warnings */}
      {errors.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg p-3 mb-3">
          {errors.map((er, idx) => <div key={idx}>• {er}</div>)}
        </div>
      )}

      {/* Preview */}
      {parsed && teams.length > 0 && !results && (
        <>
          <div className="text-xs text-gray-500 mb-2">
            Found <strong>{teams.length}</strong> team{teams.length !== 1 ? "s" : ""} · <strong>{parsed.totalPlayers}</strong> player{parsed.totalPlayers !== 1 ? "s" : ""}
          </div>
          <div className="space-y-2 mb-3 max-h-72 overflow-y-auto">
            {teams.map((t) => (
              <div key={t.id} className="p-3 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between flex-wrap gap-1">
                  <span className="font-semibold text-sm text-gray-800">{t.name}</span>
                  <span className="text-xs text-gray-500">{t.ageGroup || "no age group"} · {t.players.length} player{t.players.length !== 1 ? "s" : ""}</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-1 text-xs text-gray-500">
                  <span>parent: <code className="bg-gray-100 px-1 rounded">{t.parentPasscode}</code>{t.parentAuto && <span className="ml-1 text-blue-500">(auto)</span>}</span>
                  <span>coach: <code className="bg-gray-100 px-1 rounded">{t.coachPasscode}</code>{t.coachAuto && <span className="ml-1 text-blue-500">(auto)</span>}</span>
                </div>
                {t.players.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {t.players.map((p) => (
                      <span key={p.name} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-50 text-gray-600 text-xs rounded-full border border-gray-200">
                        {p.jersey && <span className="font-bold">#{p.jersey}</span>}{p.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {progress && committing && (
            <div className="mb-3">
              <div className="text-xs text-blue-600 mb-1 animate-pulse">Creating teams… ({progress.done}/{progress.total})</div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div className="h-2 rounded-full transition-all" style={{ width: Math.round((progress.done / progress.total) * 100) + "%", backgroundColor: BRAND.primary }} />
              </div>
            </div>
          )}

          <button
            onClick={commit}
            disabled={committing}
            className="px-6 py-2 text-white text-sm font-semibold rounded-xl shadow-sm transition-colors disabled:opacity-50"
            style={{ backgroundColor: "#16a34a" }}
          >
            {committing ? "Creating…" : `Create ${teams.length} team${teams.length !== 1 ? "s" : ""} + ${parsed.totalPlayers} player${parsed.totalPlayers !== 1 ? "s" : ""}`}
          </button>
        </>
      )}

      {/* Results */}
      {results && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
          <div className="font-semibold text-green-800 mb-1">Upload complete</div>
          <ul className="text-xs text-green-800 space-y-0.5">
            <li>✓ {results.created.length} team{results.created.length !== 1 ? "s" : ""} created</li>
            {results.existing.length > 0 && <li>• {results.existing.length} already existed — players added to them: {results.existing.join(", ")}</li>}
            <li>✓ {results.playersAdded} player{results.playersAdded !== 1 ? "s" : ""} added</li>
            {results.failed.length > 0 && (
              <li className="text-red-700">✗ {results.failed.length} team{results.failed.length !== 1 ? "s" : ""} failed: {results.failed.map((f) => f.name + " (" + f.error + ")").join("; ")}</li>
            )}
            {results.playerErrors.length > 0 && (
              <li className="text-red-700">✗ roster errors: {results.playerErrors.map((f) => f.name + " (" + f.error + ")").join("; ")}</li>
            )}
          </ul>
          <div className="flex gap-2 mt-3">
            <button onClick={reset} className="px-4 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Upload another</button>
            <button onClick={onClose} className="px-4 py-1.5 text-xs font-medium text-white rounded-lg" style={{ backgroundColor: BRAND.primary }}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}
